import React, { useState, useEffect } from "react";
import { Plus, RefreshCw, AlertCircle } from "lucide-react";
import { FaHome, FaClipboard, FaCheck } from "react-icons/fa";
import VacateWorkflowTimeline from "./VacateWorkflowTimeline";
import Modal from './Modal'
import { API_BASE } from '../config'

function decodeIdTokenEmail() {
  try {
    const t = sessionStorage.getItem("id_token");
    if (!t) return null;
    const payload = t.split(".")[1];
    const json = decodeURIComponent(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return (JSON.parse(json).email || "").toString().toLowerCase();
  } catch {
    return null;
  }
}

function decodeIdTokenCommunity() {
  try {
    const t = sessionStorage.getItem("id_token");
    if (!t) return null;
    const payload = t.split(".")[1];
    const json = decodeURIComponent(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    const data = JSON.parse(json);
    return data.community_id || data.communityId;
  } catch {
    return null;
  }
}

function getCommunityId() {
  // First try to get from JWT token
  let communityId = decodeIdTokenCommunity();
  
  // Fallback to sessionStorage if JWT doesn't have it
  if (!communityId) {
    communityId = sessionStorage.getItem("community_id");
  }
  
  return communityId;
}

async function apiFetch(path, opts = {}) {
  const token = sessionStorage.getItem("id_token");
  const resp = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { Authorization: `Bearer ${token}`, ...opts.headers },
  });
  if (!resp.ok) throw new Error(`API ${resp.status}`);
  return resp.json();
}

function getOverallStatusBadge(status) {
  const map = {
    pending:        { bg: "bg-yellow-100", text: "text-yellow-800", label: "Pending Admin Review" },
    admin_review:   { bg: "bg-purple-100", text: "text-purple-800", label: "Under Admin Review" },
    admin_approved: { bg: "bg-blue-100",   text: "text-blue-800",   label: "Awaiting Owner Approval" },
    admin_rejected: { bg: "bg-red-100",    text: "text-red-800",    label: "Rejected by Admin" },
    owner_approved: { bg: "bg-green-100",  text: "text-green-800",  label: "Fully Approved ✓" },
    owner_rejected: { bg: "bg-red-100",    text: "text-red-800",    label: "Rejected by Owner" },
  };
  const c = map[status] || map.pending;
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
}

export default function ResidentVacateRequest({ unitNbr }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const email = decodeIdTokenEmail();
  const communityId = getCommunityId();
  const [tenantName, setTenantName] = useState("");

  useEffect(() => {
    if (email) {
      setTenantName(email.split("@")[0]);
    }
  }, [email]);

  const fetchRequests = async () => {
    if (!communityId || !email) return;
    try {
      setLoading(true);
      const data = await apiFetch(`/vacate-requests?communityId=${communityId}&tenantEmail=${encodeURIComponent(email)}&emailId=${encodeURIComponent(email)}`);
      setRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching vacate requests:", err);
      setError("Failed to load vacate requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [communityId, email]);

  const handleCreateRequest = async () => {
    if (!reason.trim()) {
      setError("Please enter a reason for vacating");
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      const payload = {
        COMMUNITY_ID: communityId,
        UNIT_NBR: unitNbr,
        TENANT_EMAIL: email,
        TENANT_NAME: tenantName,
        REASON: reason,
      };

      console.log("Submitting vacate request payload:", payload);

      await apiFetch("/vacate-requests", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      setSuccess("Vacate request submitted successfully!");
      setReason("");
      setShowModal(false);
      fetchRequests();

      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error creating vacate request:", err);
      setError("Failed to submit vacate request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <FaHome className="text-lg text-blue-600" />
          <h2 className="text-lg font-bold text-gray-800">Vacate Request</h2>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-1.5 bg-blue-600 text-white px-3 py-2 rounded-lg text-sm font-medium active:bg-blue-700"
        >
          <Plus size={18} />
          New Request
        </button>
      </div>

      {/* Alert Messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-800 rounded-lg flex items-center gap-2">
          <AlertCircle size={18} />
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-100 text-green-800 rounded-lg flex items-center gap-2">
          <FaCheck size={18} />
          {success}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <Modal title="Submit Vacate Request" onClose={() => setShowModal(false)}>
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Unit Number</label>
            <input
              type="text"
              value={unitNbr}
              disabled
              className="w-full px-3 py-2 border rounded-lg bg-gray-100 text-gray-600"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Reason for Vacating</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Please provide details about your reason to vacate..."
              rows="4"
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setShowModal(false)}
              className="flex-1 px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 font-semibold"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateRequest}
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold disabled:opacity-50"
            >
              {submitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </Modal>
      )}

      {/* Requests List */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Your Vacate Requests</h3>
          <button
            onClick={fetchRequests}
            disabled={loading}
            className="p-2 hover:bg-gray-100 rounded-lg disabled:opacity-50"
          >
            <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
          </button>
        </div>

        {loading && <div className="text-center py-8 text-gray-500">Loading requests...</div>}

        {!loading && requests.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <FaClipboard className="mx-auto text-3xl mb-2 opacity-50" />
            <p>No vacate requests found</p>
          </div>
        )}

        {!loading && requests.length > 0 && (
          <div className="space-y-4">
            {requests.map((req) => (
              <div key={req.VACATE_ID} className="border rounded-lg overflow-hidden shadow-sm">
                {/* Card header */}
                <div className="flex justify-between items-center px-4 py-3 bg-gray-50 border-b">
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">Unit {req.UNIT_NBR}</p>
                    <p className="text-xs text-gray-500">
                      Submitted {new Date(req.CREATED_AT).toLocaleDateString()}
                    </p>
                  </div>
                  {getOverallStatusBadge(req.STATUS)}
                </div>
                {/* Workflow timeline */}
                <div className="px-4 py-4">
                  <VacateWorkflowTimeline request={req} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
